import { Link, useParams } from 'react-router';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { ChannelGrid } from '@/components/channel/ChannelGrid';
import { PageHeader } from '@/components/common/PageHeader';
import { ChannelGridSkeleton } from '@/components/common/Skeleton';
import {
  SPORT_SUBCATEGORIES,
  matchesSubcategory,
  sortChannelsByRelevance,
} from '@/lib/channels';

export function SportCategoryPage() {
  const { subcategory = '' } = useParams<{ subcategory: string }>();
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const isLoading = useChannelsStore((s) => s.isLoadingInitial);
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);

  const sub = SPORT_SUBCATEGORIES.find((s) => s.id === subcategory);

  if (!sub) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">Unknown sport: {subcategory}</p>
        <Link to="/" className="text-accent hover:underline mt-2 inline-block">
          ← Back to home
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <ChannelGridSkeleton count={8} />;
  }

  const filtered = sortChannelsByRelevance(
    sportsChannels.filter((c) => matchesSubcategory(c, subcategory))
  );

  return (
    <div className="flex flex-col gap-6 h-full">
      <PageHeader
        backTo="/"
        icon={<span aria-hidden="true">{sub.icon}</span>}
        title={sub.label}
        subtitle={
          filtered.length === 1
            ? '1 channel'
            : `${filtered.length.toLocaleString()} channels`
        }
      />

      <div className="flex-1 min-h-0">
        <ChannelGrid
          channels={filtered}
          onChannelClick={setCurrentChannel}
          activeChannelId={currentChannel?.id}
          columns={4}
          emptyTitle={`No ${sub.label.toLowerCase()} channels`}
          emptyDescription="The iptv-org data may not have channels tagged with these categories."
        />
      </div>

      {/* Related sports — quick navigation to sibling subcategories.
          Two-column wrap on mobile, four on larger screens. */}
      <section className="pt-2 border-t border-border">
        <PageHeader size="section" title="Related sports" />
        <div className="grid grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 gap-2 mt-3">
          {SPORT_SUBCATEGORIES.filter((s) => s.id !== sub.id).map((s) => (
            <Link
              key={s.id}
              to={`/sport/${s.id}`}
              className="flex items-center gap-2 p-3 bg-bg-elevated border border-border rounded-lg hover:border-accent hover:bg-bg-hover transition-all"
            >
              <span className="text-xl" aria-hidden="true">
                {s.icon}
              </span>
              <span className="text-sm font-medium">{s.label}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
